import * as React from 'react';
import { graphql, Link, PageProps } from 'gatsby';

import { Container } from '@smart-care/gatsby-theme-daisy/src/components/Container';
import { Layout } from '@smart-care/gatsby-theme-daisy/src/components/Layout';
import { Section } from '@smart-care/gatsby-theme-daisy/src/components/Section';
import { SEO } from '@smart-care/gatsby-theme-daisy/src/components/SEO';
import homeConfig from '@smart-care/gatsby-theme-daisy/src/config/home';

export type ServicesPageProps = PageProps<GatsbyTypes.PVHTServicesPageQuery>;

const Services = ({ data }: ServicesPageProps) => {
  return (
    <Layout>
      <SEO title="Hearing Services We Offer" />

      <Section>
        <Container>
          <h1>Services We Offer</h1>
          <p>The perfect balance between human touch and technology.</p>
        </Container>
      </Section>

      <Section>
        <Container>
          <h2>Hearing Healthcare Services</h2>

          <h3>Patient Education</h3>
          <p>
            Educating patients on the kinds of hearing loss, their causes, and
            effects on a person’s ability to hear and understand speech.
          </p>

          <h3>Hearing Evaluation</h3>
          <p>
            To accurately evaluate your level of hearing loss, a comprehensive
            hearing test is required. We perform a series of tests to better
            understand all the different aspects of your hearing loss, including
            word recognition, to determine if you are losing the ability to
            understand and interpret the sounds of speech.
          </p>

          <p>
            <Link to="/first-appointment">
              Here is what you can expect during your evaluation.
            </Link>
          </p>

          <h3>Hearing Aid Demonstration</h3>
          <p>
            There are many different levels of hearing loss, and not all hearing
            aids are appropriate for your particular level. We take the time to
            help you test various hearing aids, so you know which hearing aids
            are best for you and provide counseling on available options in all
            price ranges.
          </p>

          <h3>Hearing Aid Purchase</h3>
          <p>
            After determining the best shape, size, fit, technological
            capability, and cost of the hearing aid, we work together to enable
            you to afford the purchase. We bill your insurance (where
            applicable), take all major credit cards, and work with a finance
            company to give you an affordable monthly payment.
          </p>

          <h3>Hearing Aid Fitting</h3>
          <p>
            Your hearing aids are programmed to your specific loss, which means
            they are only as good as your evaluation results. We pride ourselves
            in taking the time during the evaluation to ensure you clearly
            understand how the process works and the necessary responses you
            should be giving, providing precise test results and, therefore,
            excellent programming of your hearing aids.
          </p>

          <p>
            You will also be instructed on how to put on, clean, and recharge
            the hearing aids.
          </p>

          <h3>Real Ear Measurement</h3>
          <p>
            Everyone's ear canal is different, which changes how the hearing aid
            preforms. Therefore, we check the sound being produced by the
            hearing aid while it is in your ear. This ensures it is producing
            the correct sound in each frequency for your loss. If necessary, we
            will adjust the hearing aid in real-time as we test. This extra step
            gives us the ability to provide you with the best possible sound
            quality for your individual hearing needs.
          </p>

          <h3>
            Why at-home hearing tests and off-the-shelf hearing aids just don't
            cut it.
          </h3>
          <p>
            At Peak View Hearing{' '}
            <Link to="/meet-the-team">our hearing healthcare providers</Link>{' '}
            can provide you with the best hearing technology available and make
            sure it is applied correctly so you can hear and understand the
            world around you.
          </p>

          <h3>Follow-up Visits</h3>
          <p>
            These ensure you are comfortable with the physical fit and use of
            the instrument in all environments. We encourage our patients to
            visit us regularly for cleaning and checking the hearing aid. We
            also recommend a new test yearly to catch any changes in your
            hearing ability and to reprogram the hearing aid to that change.
          </p>
        </Container>
      </Section>

      <Section altBackground>
        <Container>
          <h2>Hearing Aid Maintenance</h2>

          <h3>Hearing Aid Evaluation</h3>
          <p>
            We will take your current hearing aid and test it for proper
            functioning. If cleaning will return it to a working state, great.
            If more complex repairs are needed, we will do what we can to bring
            your current hearing aid back to a usable state before recommending
            the purchase of a new hearing aid.
          </p>

          <h3>Repairs on Major Brands</h3>
          <p>
            Whether due to wear and tear or because of an accident, your hearing
            aids may require repairs. Bring them in. We make repairs on all
            major brands.
          </p>

          <h3>Reprogram of Hearing Aids</h3>
          <p>
            Even if you have not purchased your hearing aids from us, we can
            reprogram your existing devices to your current level of hearing
            loss. A current version of your programming software must be
            available.
          </p>

          <h3>Batteries</h3>
          <p>
            We carry hearing aid batteries and will gladly change them out for
            you. If you’re tired of handling the small batteries, we can also
            discuss rechargeable hearing aid options.
          </p>
        </Container>
      </Section>

      <Section>
        <Container>
          <h2>Custom Ear Impressions and Hearing Protection</h2>

          <p>
            If you need customized hearing aids , we’ll take impressions to
            create the perfect fit.
          </p>

          <p>
            We also offer custom hearing protection such as earplugs for
            swimmers, hearing protection for motorcycle riders, etc.
          </p>
        </Container>
      </Section>

      <Section altBackground>
        <Container>
          <h2>Tinnitus Management</h2>

          <p>
            Hearing loss and tinnitus are often connected conditions. If you
            struggle with tinnitus with or without hearing loss, we can help.
            Depending on the severity of your tinnitus, we can discuss all the
            different tinnitus support-enabled hearing aids available to you as
            well as lifestyle changes and other management techniques.
          </p>
        </Container>
      </Section>

      <Section>
        <Container>
          <h2>Insurance</h2>

          <p>
            Did you know that your hearing devices may be covered entirely by
            your insurance?
          </p>
          <p>
            Did you also know that one small mistake in processing your claim
            can lead to you paying significantly more out of pocket then it
            should?
          </p>
          <p>
            Well-meaning hearing clinics train their front office staff, with
            little to no experience in insurance billing, to handle their claims
          </p>
          <p>
            We work with Hearing Healthcare specific Insurance Specialists to
            make sure your claim is filed quickly and correctly. Often,
            resulting in less out-of-pocket for you.
          </p>
        </Container>
      </Section>

      <Section altBackground>
        <Container>
          <h2>{homeConfig.financingRate}% Hearing Aid Financing</h2>

          <p>
            If you have insurance, we’ll help determine if and how much they are
            willing to pay for hearing aids.
          </p>
          <p>
            If you don’t have insurance and are unable to pay out-of-pocket, we
            can still help. We work with a financing company that offers
            no-interest financing for those who qualify. One finance company
            will even give you twelve months same as cash!
          </p>
        </Container>
      </Section>
    </Layout>
  );
};

export const query = graphql`
  query PVHTServicesPage {
    site {
      siteMetadata {
        organization {
          name
        }
      }
    }
  }
`;

export default Services;
